import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import FieldLabel from "components/common/FieldLabel";
import {
  useCreateAccessGroupMutation,
  useGetClientLabelsQuery,
  useUpdateAccessGroupMutation,
} from "features/accessGroups/accessGroupsApi";
import type {
  AccessCatalog,
  AccessGroup,
  ClientScopeKey,
  SurfaceKey,
  TierKey,
} from "types/interfaces/access";
import AccessGroupPermissionsPanel from "./AccessGroupPermissionsPanel";
import TierGuideDialog from "./TierGuideDialog";
import TierMatrix from "./TierMatrix";
import { appliesTo, withTier, type TierMap } from "./permissionsPanelModel";

// New groups sort after the seeded ones in the user picker.
const NEW_GROUP_SORT_ORDER = 100;

const SCOPES: { value: ClientScopeKey; label: string; hint: string }[] = [
  {
    value: "all",
    label: "Every client",
    hint: "Members see every client on the pages the group opens.",
  },
  {
    value: "labeled",
    label: "Every client with one of these labels",
    hint: "Members see every client carrying one of the labels, assigned or not.",
  },
  {
    value: "assigned",
    label: "Only the clients assigned to the person",
    hint:
      "Members see the clients assigned to them. With labels, only assigned " +
      "clients of those kinds count.",
  },
];

interface Props {
  open: boolean;
  catalog: AccessCatalog;
  group: AccessGroup | null;
  onClose: () => void;
  onSaved: (group: AccessGroup) => void;
}

function errorText(error: unknown): string {
  const data = (error as { data?: unknown })?.data;
  if (!data || typeof data !== "object") return "Could not save the group.";
  return Object.entries(data as Record<string, unknown>)
    .map(([field, value]) => {
      const text =
        typeof value === "string"
          ? value
          : Array.isArray(value)
            ? value.join(" ")
            : value && typeof value === "object"
              ? Object.entries(value as Record<string, unknown>)
                  .map(([k, v]) => `${k}: ${String(v)}`)
                  .join("; ")
              : String(value);
      return field === "error" || field === "non_field_errors"
        ? text
        : `${field}: ${text}`;
    })
    .join("\n");
}

export default function AccessGroupDialog({
  open,
  catalog,
  group,
  onClose,
  onSaved,
}: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [subHeader, setSubHeader] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [tiers, setTiers] = useState<TierMap>({});
  const [scope, setScope] = useState<ClientScopeKey>("assigned");
  const [labelSlugs, setLabelSlugs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { data: labels = [] } = useGetClientLabelsQuery(undefined, {
    skip: !open,
  });
  const [createGroup, createState] = useCreateAccessGroupMutation();
  const [updateGroup, updateState] = useUpdateAccessGroupMutation();
  const saving = createState.isLoading || updateState.isLoading;
  const fixedTiers = group?.slug === "super_admin";

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? "");
    setDescription(group?.description ?? "");
    setSubHeader(group?.sub_header ?? "");
    setTiers({ ...(group?.tiers ?? {}) });
    setScope(group?.client_scope ?? "assigned");
    setLabelSlugs(group?.client_labels ?? []);
    setError(null);
  }, [open, group]);

  const toggleLabel = (slug: string) =>
    setLabelSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  const pickedNames = labels
    .filter((label) => labelSlugs.includes(label.slug))
    .map((label) => label.name);
  const scopeText = fixedTiers
    ? appliesTo("all", [])
    : appliesTo(scope, scope === "all" ? [] : pickedNames);
  const missingLabel = scope === "labeled" && pickedNames.length === 0;

  const setTier = (surface: SurfaceKey, tier: TierKey) =>
    setTiers((prev) => withTier(catalog, prev, surface, tier));

  const handleSave = async () => {
    setError(null);
    const body = {
      name: name.trim(),
      description,
      sub_header: subHeader,
      ...(fixedTiers
        ? {}
        : {
            tiers,
            client_scope: scope,
            client_labels: scope === "all" ? [] : labelSlugs,
          }),
    };
    try {
      const saved = group
        ? await updateGroup({ id: group.id, ...body }).unwrap()
        : await createGroup({
            ...body,
            sort_order: NEW_GROUP_SORT_ORDER,
          }).unwrap();
      onSaved(saved);
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={saving ? () => undefined : onClose}
      title={group ? `Edit ${group.name}` : "New Internal Access Group"}
      maxWidth="lg"
      fullScreenBelow="md"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose} disabled={saving}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleSave}
            disabled={saving || !name.trim() || (!fixedTiers && missingLabel)}
          >
            {group ? "Save" : "Create Group"}
          </CustomButton>
        </>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 3fr) minmax(0, 2fr)",
          },
          gap: 3,
          pt: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {error && (
            <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
              {error}
            </Alert>
          )}
          <Box>
            <FieldLabel required htmlFor="access-group-name">
              Name
            </FieldLabel>
            <TextField
              id="access-group-name"
              size="small"
              fullWidth
              value={name}
              placeholder="Group name"
              onChange={(e) => setName(e.target.value)}
            />
          </Box>
          <Box>
            <FieldLabel htmlFor="access-group-description">
              Who it is for
            </FieldLabel>
            <TextField
              id="access-group-description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={description}
              placeholder="The people this group is meant for"
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>
          <Box>
            <FieldLabel htmlFor="access-group-subheader">
              Sub-header shown in the user form
            </FieldLabel>
            <TextField
              id="access-group-subheader"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={subHeader}
              placeholder="One sentence for whoever assigns this group"
              onChange={(e) => setSubHeader(e.target.value)}
            />
          </Box>
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
            >
              <FieldLabel sx={{ mb: 0 }}>Access per page</FieldLabel>
              <Tooltip title="What each tier allows">
                <IconButton
                  size="small"
                  aria-label="What each tier allows"
                  onClick={() => setGuideOpen(true)}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {fixedTiers && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Super Admin always holds every page.
              </Typography>
            )}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflowX: "auto",
              }}
            >
              <TierMatrix
                catalog={catalog}
                value={tiers}
                onChange={setTier}
                disabled={fixedTiers || saving}
              />
            </Box>
          </Box>
          <Box>
            <FieldLabel>Clients</FieldLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {fixedTiers
                ? "Super Admin always reaches every client."
                : "Which clients the pages above show. The tiers say what " +
                  "members may do there; this says for which clients."}
            </Typography>
            {!fixedTiers && (
              <>
                <RadioGroup
                  value={scope}
                  onChange={(e) => setScope(e.target.value as ClientScopeKey)}
                >
                  {SCOPES.map((option) => (
                    <Box key={option.value} sx={{ mb: 0.5 }}>
                      <FormControlLabel
                        value={option.value}
                        disabled={saving}
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {option.hint}
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: "flex-start" }}
                      />
                      {/* The labels belong to the option that uses them. */}
                      {option.value !== "all" && scope === option.value && (
                        <Box sx={{ pl: 4, pb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {option.value === "labeled"
                              ? "Labels (pick at least one):"
                              : "Count only assigned clients with these labels (optional):"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              columnGap: 2,
                            }}
                          >
                            {labels.map((label) => (
                              <FormControlLabel
                                key={label.slug}
                                disabled={saving}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={labelSlugs.includes(label.slug)}
                                    onChange={() => toggleLabel(label.slug)}
                                  />
                                }
                                label={label.name}
                              />
                            ))}
                          </Box>
                          {missingLabel && (
                            <Typography variant="caption" color="error">
                              Pick at least one label.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </RadioGroup>
              </>
            )}
          </Box>
        </Box>
        <Box>
          <AccessGroupPermissionsPanel
            catalog={catalog}
            tiers={tiers}
            subHeader={subHeader}
            appliesTo={scopeText}
          />
        </Box>
      </Box>
      <TierGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        catalog={catalog}
      />
    </AppDialog>
  );
}
